import { useId } from 'react';

import { Pagination, Keyboard, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Box, useMediaQuery, useTheme } from '@mui/material';

import { TContentMaxWidthSize, TDoctor } from '@/types';
import { useAppContext } from '@/context';

import StaffCard from '../StaffCard';
import TAGSvgIcon from '../../TAGSvgIcon';

import { getSwiperStyles } from './MeetOurStaffCarousel.styles';

const removeProviderWithNoImages = (staffCollection: TDoctor[]) => {
	return staffCollection.filter((staff) => staff?.photoUrl);
};

export default function MeetOurStaffCarousel({
	staffCollection,
	withDesktopPaginationNavigation,
	withMobileArrowsNavigation,
	contentMaxWidth = 'Medium',
}: Readonly<{
	staffCollection: TDoctor[];
	withDesktopPaginationNavigation?: boolean;
	withMobileArrowsNavigation?: boolean;
	contentMaxWidth?: TContentMaxWidthSize;
}>) {
	const id = useId();
	const theme = useTheme();
	const { config } = useAppContext();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md')); // < 900px
	const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
	const swiperPaginationProps = isSmallScreen || withDesktopPaginationNavigation ? { clickable: true } : false;
	const swiperModules = [
		Keyboard,
		isSmallScreen ? Pagination : Navigation,
		...(!isSmallScreen && withDesktopPaginationNavigation ? [Pagination] : []),
		...(isSmallScreen && withMobileArrowsNavigation ? [Navigation] : []),
	];
	const hideProviderWithNoImage = config?.featureFlags?.hideProviderWithNoImage;
	const staffsToDisplay = hideProviderWithNoImage ? removeProviderWithNoImages(staffCollection) : staffCollection;

	return (
		<Swiper
			slidesPerView={isSmallScreen ? (isExtraSmallScreen ? 1.1 : 2.1) : contentMaxWidth === 'Large' ? 3.5 : 3}
			spaceBetween={20}
			navigation={
				!isSmallScreen ||
				(isSmallScreen &&
					withMobileArrowsNavigation && {
						prevEl: `.swiper-button-prev-${CSS.escape(id)}`,
						nextEl: `.swiper-button-next-${CSS.escape(id)}`,
					})
			}
			pagination={swiperPaginationProps}
			keyboard
			modules={swiperModules}
			style={getSwiperStyles({ isSmallScreen })}
			centerInsufficientSlides
		>
			{staffsToDisplay?.map((staff, index) => (
				<SwiperSlide key={index}>
					<StaffCard
						jobTitle={staff.jobTitle}
						firstName={staff.firstName}
						lastName={staff.lastName}
						collegeName={staff.collegeName}
						isSmallScreen={isSmallScreen}
						photoUrl={staff.photoUrl}
						npi={staff.npi}
						name={staff.name}
						biography={staff.biography}
					/>
				</SwiperSlide>
			))}
			{isSmallScreen && withMobileArrowsNavigation ? (
				<Box my={3} data-test-id="navigation_card_carousel">
					<TAGSvgIcon className={`swiper-button-prev swiper-button-prev-${id}`} icon="ChevronLeftFunc" />
					<TAGSvgIcon className={`swiper-button-next swiper-button-next-${id}`} icon="ChevronRightFunc" />
				</Box>
			) : null}
		</Swiper>
	);
}
